import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import type { AccountResponse, CardResponse, InstitutionResponse } from '@clair-avenir/shared';
import { Button } from '@ds/components/core/Button';
import { Card, CardBody } from '@ds/components/core/Card';
import { Icon } from '@ds/components/core/Icon';
import { Switch } from '@ds/components/forms/Switch';
import { ArchiveConfirmDialog } from '@/components/ArchiveConfirmDialog';
import { AuthenticatedShell, Page, SectionHead } from '@/components/AppShell';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { useToast } from '@/components/ToastProvider';
import { AccountFormDialog } from '@/features/institutions/components/AccountFormDialog';
import { CardFormDialog } from '@/features/institutions/components/CardFormDialog';
import { InstitutionCard } from '@/features/institutions/components/InstitutionCard';
import { InstitutionFormDialog } from '@/features/institutions/components/InstitutionFormDialog';
import {
  useArchiveAccount,
  useArchiveCard,
  useArchiveInstitution,
  useCatalog,
  useDeleteAccount,
  useDeleteCard,
  useDeleteInstitution,
  useInstitutions,
  useUnarchiveAccount,
  useUnarchiveCard,
  useUnarchiveInstitution,
} from '@/features/institutions/api';
import { ApiError } from '@/lib/http';

export const Route = createFileRoute('/_authenticated/instituicoes')({ component: InstitutionsPage });

interface ArchiveTarget {
  kind: 'institution' | 'account' | 'card';
  id: string;
  label: string;
  cascadeCount?: number;
}

interface DeleteTarget {
  kind: 'institution' | 'account' | 'card';
  id: string;
  label: string;
}

function InstitutionsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = useState(false);
  const { data: catalog } = useCatalog();
  const { data: institutionsData, isLoading } = useInstitutions(showArchived);

  const [institutionDialog, setInstitutionDialog] = useState<{ open: boolean; institution?: InstitutionResponse }>({ open: false });
  const [accountDialog, setAccountDialog] = useState<{ open: boolean; institutionId?: string; account?: AccountResponse }>({ open: false });
  const [cardDialog, setCardDialog] = useState<{ open: boolean; institutionId?: string; card?: CardResponse }>({ open: false });
  const [archiveTarget, setArchiveTarget] = useState<ArchiveTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const archiveInstitution = useArchiveInstitution();
  const unarchiveInstitution = useUnarchiveInstitution();
  const archiveAccount = useArchiveAccount();
  const unarchiveAccount = useUnarchiveAccount();
  const archiveCard = useArchiveCard();
  const unarchiveCard = useUnarchiveCard();

  const onDeleteError = (error: unknown) => {
    if (error instanceof ApiError) toast({ tone: 'negative', title: 'Não foi possível excluir', description: error.message });
  };
  const deleteInstitution = useDeleteInstitution({ onError: onDeleteError });
  const deleteAccount = useDeleteAccount({ onError: onDeleteError });
  const deleteCard = useDeleteCard({ onError: onDeleteError });

  function handleArchiveToggleInstitution(institution: InstitutionResponse) {
    if (institution.archivedAt) {
      unarchiveInstitution.mutate(institution.id);
    } else {
      const activeAccounts = (institution.accounts ?? []).filter((a) => !a.archivedAt).length;
      const activeCards = (institution.cards ?? []).filter((c) => !c.archivedAt).length;
      setArchiveTarget({ kind: 'institution', id: institution.id, label: institution.name, cascadeCount: activeAccounts + activeCards });
    }
  }

  function handleArchiveToggleAccount(account: AccountResponse) {
    if (account.archivedAt) {
      unarchiveAccount.mutate(account.id);
    } else {
      setArchiveTarget({ kind: 'account', id: account.id, label: account.nickname });
    }
  }

  function handleArchiveToggleCard(card: CardResponse) {
    if (card.archivedAt) {
      unarchiveCard.mutate(card.id);
    } else {
      setArchiveTarget({ kind: 'card', id: card.id, label: card.nickname });
    }
  }

  function confirmArchive() {
    if (!archiveTarget) return;
    const onSuccess = () => setArchiveTarget(null);
    if (archiveTarget.kind === 'institution') archiveInstitution.mutate(archiveTarget.id, { onSuccess });
    else if (archiveTarget.kind === 'account') archiveAccount.mutate(archiveTarget.id, { onSuccess });
    else archiveCard.mutate(archiveTarget.id, { onSuccess });
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    const onSuccess = () => setDeleteTarget(null);
    if (deleteTarget.kind === 'institution') deleteInstitution.mutate(deleteTarget.id, { onSuccess });
    else if (deleteTarget.kind === 'account') deleteAccount.mutate(deleteTarget.id, { onSuccess });
    else deleteCard.mutate(deleteTarget.id, { onSuccess });
  }

  const institutions = institutionsData ?? [];

  return (
    <AuthenticatedShell title="Instituições">
      <Page>
        <div data-testid="institutions-page">
          <SectionHead
            title="Instituições"
            action={
              <Button size="sm" iconLeft={<Icon name="plus" />} data-testid="institutions-add" onClick={() => setInstitutionDialog({ open: true })}>
                Adicionar instituição
              </Button>
            }
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <Switch
              label="Mostrar arquivadas"
              data-testid="institutions-show-archived"
              checked={showArchived}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowArchived(e.target.checked)}
            />
          </div>

          {!isLoading && institutions.length === 0 ? (
            <Card>
              <CardBody>
                <EmptyState
                  testId="institutions-empty"
                  icon="landmark"
                  title="Nenhuma instituição cadastrada ainda"
                  description="Adicione seu banco, fintech ou carteira digital pra começar a acompanhar suas contas."
                  action={
                    <Button size="sm" onClick={() => setInstitutionDialog({ open: true })}>
                      Adicionar instituição
                    </Button>
                  }
                />
              </CardBody>
            </Card>
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              {institutions.map((institution) => (
                <InstitutionCard
                  key={institution.id}
                  institution={institution}
                  onEditInstitution={() => setInstitutionDialog({ open: true, institution })}
                  onArchiveToggleInstitution={() => handleArchiveToggleInstitution(institution)}
                  onDeleteInstitution={() => setDeleteTarget({ kind: 'institution', id: institution.id, label: institution.name })}
                  onAddAccount={() => setAccountDialog({ open: true, institutionId: institution.id })}
                  onEditAccount={(account) => setAccountDialog({ open: true, account })}
                  onArchiveToggleAccount={(account) => handleArchiveToggleAccount(account)}
                  onDeleteAccount={(account) => setDeleteTarget({ kind: 'account', id: account.id, label: account.nickname })}
                  onAddCard={() => setCardDialog({ open: true, institutionId: institution.id })}
                  onEditCard={(card) => setCardDialog({ open: true, card })}
                  onArchiveToggleCard={(card) => handleArchiveToggleCard(card)}
                  onDeleteCard={(card) => setDeleteTarget({ kind: 'card', id: card.id, label: card.nickname })}
                  onViewCardInvoices={(card) => navigate({ to: '/cartoes/$cardId', params: { cardId: card.id } })}
                />
              ))}
            </div>
          )}
        </div>

        <InstitutionFormDialog
          open={institutionDialog.open}
          onClose={() => setInstitutionDialog({ open: false })}
          catalog={catalog ?? []}
          institution={institutionDialog.institution}
        />
        <AccountFormDialog
          open={accountDialog.open}
          onClose={() => setAccountDialog({ open: false })}
          institutionId={accountDialog.institutionId}
          account={accountDialog.account}
        />
        <CardFormDialog
          open={cardDialog.open}
          onClose={() => setCardDialog({ open: false })}
          institutionId={cardDialog.institutionId}
          card={cardDialog.card}
        />
        <ArchiveConfirmDialog
          open={!!archiveTarget}
          entityLabel={archiveTarget?.label ?? ''}
          cascadeCount={archiveTarget?.cascadeCount}
          loading={archiveInstitution.isPending || archiveAccount.isPending || archiveCard.isPending}
          onClose={() => setArchiveTarget(null)}
          onConfirm={confirmArchive}
        />
        <DeleteConfirmDialog
          open={!!deleteTarget}
          entityLabel={deleteTarget?.label ?? ''}
          loading={deleteInstitution.isPending || deleteAccount.isPending || deleteCard.isPending}
          onClose={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      </Page>
    </AuthenticatedShell>
  );
}
